import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useRoles } from '../api/roles-api';
import { useInviteMember } from '../api/members-api';
import { useIsPersonalEdition } from '@/lib/auth/useEdition';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const DEFAULT_PERSONAL_ROLE_NAME = 'Editor';

export function InviteUserDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');

  const { data: roles } = useRoles();
  const inviteMember = useInviteMember();
  const isPersonal = useIsPersonalEdition();

  // In personal edition, default the role dropdown to Editor (looked up by
  // name so it survives role-id changes). Only auto-set when the user has
  // not made an explicit selection yet.
  useEffect(() => {
    if (!isPersonal || roleId) return;
    const editor = (roles ?? []).find((r) => r.name === DEFAULT_PERSONAL_ROLE_NAME);
    if (editor) setRoleId(editor.id);
  }, [isPersonal, roles, roleId]);

  function resetForm() {
    setName('');
    setEmail('');
    setRoleId('');
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !roleId) return;

    try {
      await inviteMember.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        role_id: roleId,
      });
      toast.success(isPersonal ? 'Invitation sent' : 'Invitation sent successfully');
      resetForm();
      setOpen(false);
    } catch {
      toast.error('Failed to send invitation');
    }
  }

  const triggerLabel = isPersonal ? 'Share access' : 'Invite User';
  const dialogTitle = isPersonal ? 'Share access' : 'Invite User';
  const submitLabel = isPersonal ? 'Send invite' : 'Send Invitation';

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) resetForm();
      }}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-name">Name</Label>
            <Input
              id="invite-name"
              required
              value={name}
              placeholder="Full name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              required
              type="email"
              value={email}
              placeholder="user@example.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-role">Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Select role..." />
              </SelectTrigger>
              <SelectContent>
                {(roles ?? []).map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md bg-success-surface border border-success-border px-3 py-2 text-xs text-success">
            An invitation email will be sent to the user with a link to set their password. The link expires in 24
            hours.
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={inviteMember.isPending}>
              {inviteMember.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
